import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Clock } from "lucide-react";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  HOUR_OPTIONS, MINUTE_OPTIONS, Period,
  compareTimes, formatDisplay, parseTime, to12h, to24h,
  WORK_END_HOUR, WORK_START_HOUR,
} from "@/lib/work-hours";

export interface WorkHoursTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  error?: string;
  /** When set, the picker disables cells whose resulting time is <= minTime. */
  minTime?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

const PURPLE = "#7C3AED";
const PURPLE_DARK = "#6D28D9";
const PURPLE_SOFT = "#F5F3FF";
const NEUTRAL_BORDER = "#E5E7EB";
const TEXT_BASE = "#18181B";
const TEXT_MUTED = "#9CA3AF";

// Layout constants — must stay in sync between Column padding & highlight bar.
const ROW_H = 36;          // px height per row
const VISIBLE_ROWS = 5;    // odd number so a row sits dead-center
const COL_H = ROW_H * VISIBLE_ROWS;          // 180
const PAD_Y = ROW_H * Math.floor(VISIBLE_ROWS / 2); // 72 — top/bottom padding so edges can center

export function WorkHoursTimePicker({
  value, onChange, label, required, error, minTime, disabled, className, id,
}: WorkHoursTimePickerProps) {
  const parsed = parseTime(value);
  const initial = parsed ? to12h(parsed.hour24) : { hour12: 9, period: "AM" as Period };
  const [hour12, setHour12] = React.useState<number>(initial.hour12);
  const [minute, setMinute] = React.useState<number>(parsed?.minute ?? 0);
  const [period, setPeriod] = React.useState<Period>(initial.period);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!parsed) return;
    const t = to12h(parsed.hour24);
    setHour12(t.hour12);
    setPeriod(t.period);
    setMinute(parsed.minute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const withinWork = (h12: number, m: number, p: Period): boolean => {
    const h24 = to24h(h12, p);
    const total = h24 * 60 + m;
    return total >= WORK_START_HOUR * 60 && total <= WORK_END_HOUR * 60;
  };

  const periodValid = (p: Period, h12: number): boolean => {
    if (p === "AM") return h12 >= 9 && h12 <= 11;
    if (h12 === 12 || (h12 >= 1 && h12 <= 6)) return true;
    if (h12 === 7) return true;
    return false;
  };

  const beforeMin = (h12: number, m: number, p: Period): boolean => {
    if (!minTime) return false;
    const candidate = `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${p}`;
    return compareTimes(candidate, minTime) <= 0;
  };

  const resolvePeriod = (h12: number, prefer: Period): Period => {
    if (periodValid(prefer, h12)) return prefer;
    return prefer === "AM" ? "PM" : "AM";
  };

  const resolveMinute = (h12: number, p: Period, prefer: number): number => {
    if (withinWork(h12, prefer, p) && !beforeMin(h12, prefer, p)) return prefer;
    for (const m of MINUTE_OPTIONS) {
      if (withinWork(h12, m, p) && !beforeMin(h12, m, p)) return m;
    }
    return prefer;
  };

  const apply = (h: number, m: number, p: Period) => {
    const next = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} ${p}`;
    onChange(next);
  };

  const onPickHour = (h: number) => {
    const p = resolvePeriod(h, period);
    const m = resolveMinute(h, p, minute);
    setHour12(h); setPeriod(p); setMinute(m);
    apply(h, m, p);
  };
  const onPickMinute = (m: number) => {
    setMinute(m); apply(hour12, m, period);
  };
  const onPickPeriod = (p: Period) => {
    const m = resolveMinute(hour12, p, minute);
    setPeriod(p); setMinute(m); apply(hour12, m, p);
  };

  const display = formatDisplay(value);

  return (
    <div className={cn("space-y-1.5", className)}>
      {label ? (
        <Label
          htmlFor={id}
          className="text-[11px] font-semibold uppercase tracking-[0.4px]"
          style={{ color: PURPLE_DARK }}
        >
          {label}{required && <span className="ml-0.5" style={{ color: "#DC2626" }}>*</span>}
        </Label>
      ) : (
        <div aria-hidden className="text-[11px] font-semibold uppercase tracking-[0.4px] opacity-0 select-none">&nbsp;</div>
      )}
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            style={{
              borderColor: error ? "#DC2626" : open ? PURPLE : NEUTRAL_BORDER,
              boxShadow: open ? `0 0 0 4px ${PURPLE}1F` : undefined,
              color: TEXT_BASE,
            }}
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-lg border bg-white px-3 py-1 text-[13px] font-medium shadow-sm transition-all duration-150",
              "hover:border-[#C4B5FD] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <span className={cn("tabular-nums", !display && "text-[#9CA3AF] font-normal")}>
              {display || "--:-- --"}
            </span>
            <Clock className="h-4 w-4" style={{ color: open ? PURPLE : TEXT_MUTED }} />
          </button>
        </PopoverTrigger>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-50 w-auto overflow-hidden rounded-xl border bg-white p-0 text-[#18181B] shadow-[0_10px_30px_-10px_rgba(124,58,237,0.25),0_4px_12px_-4px_rgba(0,0,0,0.08)] outline-none"
          style={{ borderColor: NEUTRAL_BORDER }}
        >
          {/* Column headers */}
          <div
            className="grid grid-cols-3 border-b text-center"
            style={{ borderColor: NEUTRAL_BORDER, background: PURPLE_SOFT }}
          >
            {["Hours", "Minutes", "AM/PM"].map((h) => (
              <div
                key={h}
                className="py-2 text-[10px] font-semibold uppercase tracking-[0.5px]"
                style={{ color: PURPLE_DARK }}
              >
                {h}
              </div>
            ))}
          </div>

          <div className="relative flex" style={{ background: "#FFFFFF", height: COL_H }}>
            {/* Selected highlight bar — centered row */}
            <div
              aria-hidden
              className="pointer-events-none absolute left-2 right-2 rounded-lg"
              style={{
                top: (COL_H - ROW_H) / 2,
                height: ROW_H,
                background: PURPLE,
                boxShadow: `0 6px 16px -6px ${PURPLE}80`,
              }}
            />
            {/* Top + bottom fade masks */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 z-10"
              style={{ height: PAD_Y, background: "linear-gradient(to bottom, #FFFFFF 30%, rgba(255,255,255,0))" }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 z-10"
              style={{ height: PAD_Y, background: "linear-gradient(to top, #FFFFFF 30%, rgba(255,255,255,0))" }}
            />

            <Column
              items={HOUR_OPTIONS as readonly number[]}
              selectedIndex={HOUR_OPTIONS.indexOf(hour12 as typeof HOUR_OPTIONS[number])}
              isDisabled={() => false}
              format={(h) => String(h).padStart(2, "0")}
              onPick={(h) => onPickHour(h as number)}
            />
            <Divider />
            <Column
              items={MINUTE_OPTIONS}
              selectedIndex={MINUTE_OPTIONS.indexOf(minute)}
              isDisabled={(m) => !withinWork(hour12, m, period) || beforeMin(hour12, m, period)}
              format={(m) => String(m).padStart(2, "0")}
              onPick={(m) => onPickMinute(m)}
            />
            <Divider />
            <Column
              items={["AM", "PM"] as Period[]}
              selectedIndex={period === "AM" ? 0 : 1}
              isDisabled={(p) => !periodValid(p as Period, hour12)}
              format={(p) => p as string}
              onPick={(p) => onPickPeriod(p as Period)}
            />
          </div>

          <div
            className="flex items-center justify-center gap-1.5 border-t px-3 py-2 text-[10px] font-medium"
            style={{ borderColor: NEUTRAL_BORDER, color: PURPLE_DARK, background: PURPLE_SOFT }}
          >
            <Clock className="h-3 w-3" />
            <span>Working hours: 9:00 AM – 7:00 PM · 5-min steps</span>
          </div>
        </PopoverPrimitive.Content>
      </Popover>
      {error && <p className="text-[11px]" style={{ color: "#DC2626" }}>{error}</p>}
    </div>
  );
}

function Divider() {
  return <div aria-hidden className="w-px self-stretch" style={{ background: NEUTRAL_BORDER }} />;
}

function Column<T>({
  items, selectedIndex, isDisabled, format, onPick,
}: {
  items: readonly T[];
  selectedIndex: number;
  isDisabled: (item: T) => boolean;
  format: (item: T) => string;
  onPick: (item: T) => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const programmatic = React.useRef(false);

  // Scroll selected item to center whenever it changes.
  React.useEffect(() => {
    const el = ref.current;
    if (!el || selectedIndex < 0) return;
    programmatic.current = true;
    el.scrollTo({ top: selectedIndex * ROW_H, behavior: "smooth" });
    const t = setTimeout(() => { programmatic.current = false; }, 350);
    return () => clearTimeout(t);
  }, [selectedIndex]);

  // Snap-driven picking: when user scrolls, pick the item that lands in center.
  const onScroll = React.useCallback(() => {
    if (programmatic.current) return;
    const el = ref.current;
    if (!el) return;
    // Debounce via rAF
    window.requestAnimationFrame(() => {
      const idx = Math.round(el.scrollTop / ROW_H);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      const item = items[clamped];
      if (item !== undefined && !isDisabled(item) && clamped !== selectedIndex) {
        onPick(item);
      }
    });
  }, [items, isDisabled, onPick, selectedIndex]);

  return (
    <div
      ref={ref}
      onScroll={onScroll}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      className="relative w-20 overflow-y-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={{
        height: COL_H,
        scrollSnapType: "y mandatory",
        paddingTop: PAD_Y,
        paddingBottom: PAD_Y,
      }}
    >
      {items.map((item, i) => {
        const selected = i === selectedIndex;
        const disabled = isDisabled(item);
        return (
          <button
            key={i}
            type="button"
            disabled={disabled}
            onClick={() => onPick(item)}
            style={{ height: ROW_H, scrollSnapAlign: "center", scrollSnapStop: "always" }}
            className={cn(
              "relative z-20 flex w-full items-center justify-center text-[14px] tabular-nums font-medium transition-colors duration-150",
              selected ? "text-white" : "text-[#18181B] hover:text-[#6D28D9]",
              disabled && "opacity-25 cursor-not-allowed pointer-events-none",
            )}
          >
            {format(item)}
          </button>
        );
      })}
    </div>
  );
}
